import { useState, useEffect } from 'react';
import { 
  Globe, 
  Edit,
  Check,
  DollarSign,
  Percent,
  MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { countryAPI } from '../lib/api';
import { toast } from 'sonner';

export default function Countries() {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const response = await countryAPI.getAll();
      setCountries(response.data);
    } catch (error) {
      toast.error('Failed to fetch countries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  const handleUpdate = async () => {
    try {
      await countryAPI.update(selectedCountry.country_code, selectedCountry);
      toast.success('Country settings updated');
      setEditDialog(false);
      fetchCountries();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleToggleEnabled = async (country) => {
    try {
      await countryAPI.update(country.country_code, { is_enabled: !country.is_enabled });
      toast.success(`${country.country_name} ${!country.is_enabled ? 'enabled' : 'disabled'}`);
      fetchCountries();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openEditDialog = (country) => {
    setSelectedCountry({ ...country });
    setEditDialog(true);
  };

  const currencySymbols = {
    INR: '₹',
    USD: '$',
    AED: 'د.إ',
    GBP: '£',
    AUD: 'A$',
  };

  return (
    <div data-testid="countries-page">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Country Settings</h1>
        <p className="page-subtitle">Configure country-specific rules and taxes</p>
      </div>

      {/* Stats */}
      <div className="widget-grid mb-6">
        <div className="stat-card">
          <Globe className="w-5 h-5 text-primary mb-2" />
          <p className="stat-value">{countries.filter(c => c.is_enabled).length}</p>
          <p className="stat-label">Enabled Countries</p>
        </div>
        <div className="stat-card">
          <DollarSign className="w-5 h-5 text-emerald-400 mb-2" />
          <p className="stat-value">{countries.length}</p>
          <p className="stat-label">Total Currencies</p>
        </div>
        <div className="stat-card">
          <MapPin className="w-5 h-5 text-amber-400 mb-2" />
          <p className="stat-value">{countries.reduce((sum, c) => sum + (c.restricted_states?.length || 0), 0)}</p>
          <p className="stat-label">Restricted Regions</p>
        </div>
      </div>

      {/* Table */}
      <div className="data-table-container">
        <div className="data-table-header">
          <h3 className="data-table-title">Country Configuration</h3>
        </div>

        <Table>
          <TableHeader>
            <TableRow className="table-header">
              <TableHead>Country</TableHead>
              <TableHead>Currency</TableHead>
              <TableHead>Min Age</TableHead>
              <TableHead>GST %</TableHead>
              <TableHead>TDS %</TableHead>
              <TableHead>Rake %</TableHead>
              <TableHead>Restricted States</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                </TableCell>
              </TableRow>
            ) : countries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                  No countries configured
                </TableCell>
              </TableRow>
            ) : (
              countries.map((country) => (
                <TableRow key={country.country_code} className="table-row" data-testid={`country-row-${country.country_code}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-muted flex items-center justify-center text-xs font-bold">
                        {country.country_code}
                      </div>
                      <span className="font-medium">{country.country_name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono">
                    {currencySymbols[country.currency] || country.currency} ({country.currency})
                  </TableCell>
                  <TableCell>{country.min_age}+</TableCell>
                  <TableCell className="font-mono">{country.gst_percentage}%</TableCell>
                  <TableCell className="font-mono">{country.tds_percentage}%</TableCell>
                  <TableCell className="font-mono">{country.rake_percentage}%</TableCell>
                  <TableCell>
                    {country.restricted_states?.length > 0 ? (
                      <span className="badge badge-warning">
                        {country.restricted_states.length} states
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={country.is_enabled}
                      onCheckedChange={() => handleToggleEnabled(country)}
                      data-testid={`toggle-country-${country.country_code}`}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditDialog(country)}
                      data-testid={`edit-country-${country.country_code}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit {selectedCountry?.country_name}</DialogTitle>
          </DialogHeader>
          {selectedCountry && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Min Age</Label>
                  <Input
                    type="number"
                    value={selectedCountry.min_age}
                    onChange={(e) => setSelectedCountry({ ...selectedCountry, min_age: parseInt(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rake %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={selectedCountry.rake_percentage}
                    onChange={(e) => setSelectedCountry({ ...selectedCountry, rake_percentage: parseFloat(e.target.value) })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>GST %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={selectedCountry.gst_percentage}
                    onChange={(e) => setSelectedCountry({ ...selectedCountry, gst_percentage: parseFloat(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>TDS %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={selectedCountry.tds_percentage}
                    onChange={(e) => setSelectedCountry({ ...selectedCountry, tds_percentage: parseFloat(e.target.value) })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>TDS Threshold</Label>
                <Input
                  type="number"
                  value={selectedCountry.tds_threshold}
                  onChange={(e) => setSelectedCountry({ ...selectedCountry, tds_threshold: parseFloat(e.target.value) })}
                />
                <p className="text-xs text-muted-foreground">TDS applied on winnings above this amount</p>
              </div>
              <div className="space-y-2">
                <Label>Restricted States (comma separated)</Label>
                <Input
                  placeholder="e.g., Assam, Telangana"
                  value={selectedCountry.restricted_states?.join(', ') || ''}
                  onChange={(e) => setSelectedCountry({ 
                    ...selectedCountry, 
                    restricted_states: e.target.value ? e.target.value.split(',').map(s => s.trim()) : []
                  })}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Enable Country</Label>
                <Switch
                  checked={selectedCountry.is_enabled}
                  onCheckedChange={(v) => setSelectedCountry({ ...selectedCountry, is_enabled: v })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdate} data-testid="save-country-btn">
              <Check className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
